import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { useModules } from '../../../module-system/use-modules.js';
import { callsRequest } from '../hooks/calls-api.js';
import { isActiveAttempt } from '../model/calls.model.js';

const EMPTY = { enabled: false, canView: false, canManage: false, canDial: false, canTeam: false, canSettings: false, active: [], editor: null, revision: 0, starting: false, error: '', token: null };
const CallsContext = createContext(EMPTY);
export const useCalls = () => useContext(CallsContext);

export function CallsProvider({ children }) {
  const { token, permissions = [] } = useAuth();
  const modules = useModules(token);
  const enabled = Boolean(token && modules.some((item) => item.key === 'calls' && item.enabled));
  const canView = enabled && permissions.includes('calls.view');
  const canManage = canView && permissions.includes('calls.manage');
  const canDial = canView && permissions.includes('calls.dial');
  const canTeam = canView && permissions.includes('calls.team');
  const canSettings = enabled && permissions.includes('calls.settings');
  const scope = `${window.location.origin}:${token || ''}:${enabled}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const [state, setState] = useState({ ...EMPTY, scope });
  const startLock = useRef(false);
  const current = state.scope === scope ? state : EMPTY;
  const activeRef = useRef(current.active);
  activeRef.current = current.active;

  useEffect(() => {
    setState({ ...EMPTY, scope });
    startLock.current = false;
    if (!canView) return undefined;
    let disposed = false;
    let running = false;
    async function refresh() {
      if (running) return;
      running = true;
      try {
        const updates = [];
        for (const attempt of activeRef.current.filter(isActiveAttempt)) {
          try {
            const payload = await callsRequest(token, `/attempts/${encodeURIComponent(attempt.id)}/sync`, 'POST', {});
            if (payload.attempt) updates.push(payload.attempt);
          } catch { /* A reserved or uncertain call may have no remote reference yet. Keep reading CRM state. */ }
        }
        const payload = await callsRequest(token, '/active');
        if (disposed || scopeRef.current !== scope) return;
        const known = Array.isArray(payload.items) ? payload.items : [];
        setState((previous) => {
          const next = new Map(previous.active.map((item) => [item.id, item]));
          [...updates, ...known].forEach((item) => {
            if ((next.get(item.id)?.version || 0) <= (item.version || 0)) next.set(item.id, item);
          });
          return { ...previous, scope, active: [...next.values()], revision: previous.revision + (updates.some((item) => !isActiveAttempt(item)) ? 1 : 0), error: '' };
        });
      } catch {
        if (!disposed && scopeRef.current === scope) setState((previous) => ({ ...previous, error: 'calls.syncError' }));
      } finally { running = false; }
    }
    refresh();
    const timer = setInterval(refresh, 10000);
    return () => { disposed = true; clearInterval(timer); };
  }, [token, scope, canView]);

  const openEditor = useCallback((mode, record = {}) => {
    if (canManage) setState((previous) => ({ ...previous, scope, editor: { mode, record } }));
  }, [canManage, scope]);
  const closeEditor = useCallback(() => setState((previous) => ({ ...previous, editor: null })), []);
  const changed = useCallback(() => setState((previous) => ({ ...previous, revision: previous.revision + 1 })), []);
  const updateAttempt = useCallback((attempt) => {
    if (attempt?.id) setState((previous) => ({ ...previous, active: previous.active.map((item) => item.id === attempt.id ? attempt : item) }));
  }, []);
  const dismiss = useCallback((id) => setState((previous) => ({ ...previous, active: previous.active.filter((item) => item.id !== id) })), []);
  const startCall = useCallback(async (profileId, taskId = null) => {
    if (!canDial || !profileId || startLock.current || current.active.some(isActiveAttempt)) return;
    startLock.current = true;
    setState((previous) => ({ ...previous, scope, starting: true, error: '' }));
    try {
      const payload = await callsRequest(token, '/attempts/start', 'POST', { profileId, taskId, requestId: crypto.randomUUID() });
      if (scopeRef.current === scope && payload.attempt) setState((previous) => ({ ...previous, active: [...previous.active.filter((item) => item.id !== payload.attempt.id), payload.attempt], revision: previous.revision + 1 }));
    } catch {
      if (scopeRef.current === scope) setState((previous) => ({ ...previous, error: 'calls.startError' }));
    } finally {
      if (scopeRef.current === scope) {
        startLock.current = false;
        setState((previous) => ({ ...previous, starting: false }));
      }
    }
  }, [canDial, current.active, token, scope]);

  const value = useMemo(() => ({ ...current, token, enabled, canView, canManage, canDial, canTeam, canSettings, openEditor, closeEditor, changed, dismiss, updateAttempt, startCall }), [current, token, enabled, canView, canManage, canDial, canTeam, canSettings, openEditor, closeEditor, changed, dismiss, updateAttempt, startCall]);
  return <CallsContext.Provider value={value}>{children}</CallsContext.Provider>;
}
